import React, { useEffect, useRef } from 'react';
import Spline from '@splinetool/react-spline';
import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const experiences = [
  {
    id: 1,
    role: 'Senior Frontend Developer',
    company: 'Tech Innovations Inc.',
    duration: 'Jan 2023 - Present',
    description: 'Leading the frontend team in building scalable web applications using React, TypeScript, and modern CSS. Implemented micro-frontend architecture serving 1M+ users.',
    technologies: ['React', 'TypeScript', 'Next.js', 'GraphQL'],
  },
  {
    id: 2,
    role: 'Frontend Developer',
    company: 'Digital Solutions Ltd.',
    duration: 'Jun 2021 - Dec 2022',
    description: 'Developed responsive web applications and interactive UI components. Optimized performance resulting in 40% faster load times. Collaborated with design team on 3D web experiences.',
    technologies: ['React', 'JavaScript', 'GSAP', 'Three.js'],
  },
  {
    id: 3,
    role: 'Junior Web Developer',
    company: 'Creative Agency Co.',
    duration: 'Mar 2020 - May 2021',
    description: 'Built and maintained client websites with focus on animations and user experience. Implemented SEO best practices and accessibility standards across all projects.',
    technologies: ['HTML5', 'CSS3', 'JavaScript', 'WordPress'],
  },
  {
    id: 4,
    role: 'Web Development Intern',
    company: 'StartUp Hub',
    duration: 'Sep 2019 - Feb 2020',
    description: 'Assisted in developing the company\'s main product dashboard. Learned agile methodologies and modern development workflows. Built reusable component library.',
    technologies: ['React', 'Material UI', 'Git', 'Figma'],
  },
];

const Experience = () => {
  const sectionRef = useRef(null);
  const timelineRef = useRef(null);
  const cardsRef = useRef([]);

  useEffect(() => {
    const section = sectionRef.current;
    const cards = cardsRef.current;

    // Title animation
    gsap.from('.experience-title', {
      scrollTrigger: {
        trigger: section,
        start: 'top 80%',
        end: 'top 40%',
        scrub: 1,
      },
      y: 80,
      opacity: 0,
      scale: 0.9,
    });

    gsap.from('.experience-subtitle', {
      scrollTrigger: {
        trigger: section,
        start: 'top 70%',
        end: 'top 35%',
        scrub: 1,
      },
      y: 40,
      opacity: 0,
    });

    // Timeline line grow animation
    gsap.from('.timeline-line', {
      scrollTrigger: {
        trigger: timelineRef.current,
        start: 'top 80%',
        end: 'bottom 60%',
        scrub: 1,
      },
      scaleY: 0,
      transformOrigin: 'top center',
    });

    // Cards animation with alternating direction
    cards.forEach((card, index) => {
      const direction = index % 2 === 0 ? -100 : 100;
      gsap.from(card, {
        scrollTrigger: {
          trigger: card,
          start: 'top 85%',
          end: 'top 60%',
          scrub: 1,
        },
        x: direction,
        opacity: 0,
        rotateY: index % 2 === 0 ? 10 : -10,
      });

      // Dot pulse animation
      gsap.from(card.querySelector('.timeline-dot'), {
        scrollTrigger: {
          trigger: card,
          start: 'top 80%',
          toggleActions: 'play none none reverse',
        },
        scale: 0,
        duration: 0.5,
        ease: 'back.out(1.7)',
      });
    });

    // Spline parallax
    gsap.to('.experience-spline-container', {
      scrollTrigger: {
        trigger: section,
        start: 'top bottom',
        end: 'bottom top',
        scrub: true,
      },
      yPercent: -15,
      rotation: 5,
      ease: 'none',
    });
  }, []);

  return (
    <section className='experience' id='experience' ref={sectionRef}>
      <div className='experience-spline-container'>
        <Spline scene="https://prod.spline.design/6Wq1Q7YGyM-iab9i/scene.splinecode" className='experience-spline' />
      </div>

      <div className='experience-content'>
        <h2 className='experience-title'>Experience</h2>
        <p className='experience-subtitle'>My professional journey so far</p>

        <div className='timeline' ref={timelineRef}>
          <div className='timeline-line'></div>

          {experiences.map((exp, index) => (
            <div
              className={`timeline-item ${index % 2 === 0 ? 'left' : 'right'}`}
              key={exp.id}
              ref={(el) => (cardsRef.current[index] = el)}
            >
              <div className='timeline-dot'></div>
              <div className='timeline-card'>
                <div className='timeline-card-header'>
                  <h3>{exp.role}</h3>
                  <span className='company'>{exp.company}</span>
                  <span className='duration'>{exp.duration}</span>
                </div>
                <p>{exp.description}</p>
                <div className='exp-tech-stack'>
                  {exp.technologies.map((tech, i) => (
                    <span key={i} className='exp-tech-tag'>{tech}</span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Experience;
